import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { FiStar } from 'react-icons/fi';
import { MatchData } from '@/interfaces/liveGamesType';
import { GroupedMatches } from '../../types';
import {
  MatchItem,
  SelectedMatchItem,
  LiveMatchItem,
  MatchListContainer,
  LeagueHeaderContainer
} from '../../styles';
import { isMatchLive } from '../../utils';

interface MatchesListProps {
  matches: MatchData[];
  loading: boolean;
  selectedMatch: MatchData | null;
  favorites: string[];
  groupedMatches: GroupedMatches;
  onSelectMatch: (match: MatchData) => void;
  toggleFavorite: (id: string) => void;
  isMobile?: boolean;
}

const MatchesList: React.FC<MatchesListProps> = ({
  matches,
  loading,
  selectedMatch,
  favorites,
  groupedMatches,
  onSelectMatch,
  toggleFavorite,
  isMobile
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (matches.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body1">Nenhuma partida encontrada</Typography>
      </Box>
    );
  }

  const isTeamHomeLooser = (match: MatchData) => {
    return Number(match.match_hometeam_score) < Number(match.match_awayteam_score);
  };

  const getLooserOpacity = (isLooser: boolean) => isLooser ? 0.7 : 1;

  return (
    <MatchListContainer>
      {Object.entries(groupedMatches).map(([leagueId, data]) => (
        <Box key={leagueId} mb={2} component="section" aria-label={data.league}>
          {/* Header da Liga */}
          <LeagueHeaderContainer>
            <Box display="flex" alignItems="center" width="100%">
              <Box
                component="img"
                src={`https://apiv3.apifootball.com/badges/logo_leagues/${leagueId}_logo.png`}
                alt={data.country}
                width={24}
                height={24}
                mr={1}
                onError={(e: any) => { e.target.src = 'https://placehold.co/24' }}
              />
              <Typography variant="subtitle2">{data.league}</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => toggleFavorite(leagueId)}
              aria-label={favorites.includes(leagueId) ? "Remover dos favoritos" : "Adicionar aos favoritos"}
            >
              {favorites.includes(leagueId)
                ? <FiStar fill="currentColor" />
                : <FiStar />
              }
            </IconButton>
          </LeagueHeaderContainer>

          {/* Lista de Partidas */}
          {data.matches.map(match => {
            const matchIsLive = isMatchLive(match);
            const isSelected = selectedMatch?.match_id === match.match_id;
            const homeTeamIsLooser = isTeamHomeLooser(match);
            const awayTeamIsLooser = !homeTeamIsLooser;

            // Escolher o componente base com base no estado da partida
            const MatchItemComponent = isSelected
              ? SelectedMatchItem
              : matchIsLive
                ? LiveMatchItem
                : MatchItem;

            return (
              <MatchItemComponent
                key={match.match_id}
                elevation={0}
                onClick={() => onSelectMatch(match)}
                aria-label={`${match.match_hometeam_name} ${match.match_hometeam_score}-${match.match_awayteam_score} ${match.match_awayteam_name}`}
              >
                {/* Horário e Status */}
                <Box width="40px" textAlign="center">
                  <Typography variant="caption" color="textSecondary">
                    {match.match_time}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={matchIsLive ? "success.main" : "textSecondary"}
                    display="block"
                    fontWeight={matchIsLive ? "bold" : "normal"}
                  >
                    {matchIsLive ? '• LIVE' : match.match_status}
                  </Typography>
                </Box>

                {isMobile ? (
                  <Box display="flex" flexDirection="column" flex={1} pl={1}>
                    {/* Time Visitante */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%" 
                      mb={1}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          component="img"
                          src={match.team_away_badge}
                          alt={match.match_awayteam_name}
                          width={24}
                          height={24}
                          mr={1}
                          onError={(e: any) => { e.target.src = 'https://placehold.co/24' }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ opacity: getLooserOpacity(awayTeamIsLooser) }}
                        >
                          {match.match_awayteam_name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ opacity: getLooserOpacity(awayTeamIsLooser) }}
                      >
                        {match.match_awayteam_score}
                      </Typography>
                    </Box>

                    {/* Time da Casa */}
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="space-between" 
                      width="100%"
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          component="img"
                          src={match.team_home_badge}
                          alt={match.match_hometeam_name}
                          width={24}
                          height={24}
                          mr={1}
                          onError={(e: any) => { e.target.src = 'https://placehold.co/24' }}
                        />
                        <Typography 
                          variant="body2"
                          sx={{ opacity: getLooserOpacity(homeTeamIsLooser) }}
                        >
                          {match.match_hometeam_name}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        sx={{ opacity: getLooserOpacity(homeTeamIsLooser) }}
                      >
                        {match.match_hometeam_score}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Time da Casa */}
                    <Box display="flex" alignItems="center" justifyContent="flex-end" flex={1} pr={1}>
                      <Typography variant="body2" align="right">{match.match_hometeam_name}</Typography>
                      <Box
                        component="img"
                        src={match.team_home_badge}
                        alt={match.match_hometeam_name}
                        width={24}
                        height={24}
                        ml={1}
                        onError={(e: any) => { e.target.src = 'https://placehold.co/24' }}
                      />
                    </Box>

                    {/* Placar */}
                    <Box mx={1} minWidth={30} textAlign="center">
                      <Typography variant="body2" fontWeight="bold">
                        {match.match_hometeam_score} - {match.match_awayteam_score}
                      </Typography>
                    </Box>

                    {/* Time Visitante */}
                    <Box display="flex" alignItems="center" flex={1} pl={1}>
                      <Box
                        component="img"
                        src={match.team_away_badge}
                        alt={match.match_awayteam_name}
                        width={24}
                        height={24}
                        mr={1}
                        onError={(e: any) => { e.target.src = 'https://placehold.co/24' }}
                      />
                      <Typography variant="body2">{match.match_awayteam_name}</Typography>
                    </Box>
                  </>
                )}

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(match.match_id || '');
                  }}
                  aria-label={favorites.includes(match.match_id || '') ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                >
                  {favorites.includes(match.match_id || '')
                    ? <FiStar fill="currentColor" />
                    : <FiStar />
                  }
                </IconButton>
              </MatchItemComponent>
            );
          })}
        </Box>
      ))}
    </MatchListContainer>
  );
};

export default MatchesList;